// Vue Router 4 instance for the redesigned ThinkSpatial marketing site.
//
// The full route table is always registered in code (Requirements 2.2, 12.1):
// the hidden Software, Login, and Blog destinations are reachable by direct URL
// regardless of their feature flags — only their navigation links are gated
// (see resolveNavItems). All Primary_Menu navigation is client-side via history
// mode, so moving between pages never triggers a full page reload
// (Requirements 1.3, 13.2). Route names/paths match how the pages reference one
// another (e.g. ProjectsPage links to { name: 'project-detail' }).
import { createRouter, createWebHistory } from 'vue-router';

import HomePage from 'pages/HomePage.vue';
import AboutPage from 'pages/AboutPage.vue';
import ProjectsPage from 'pages/ProjectsPage.vue';
import ServicesPage from 'pages/ServicesPage.vue';
import ProjectDetailPage from 'pages/ProjectDetailPage.vue';
import ContactPage from 'pages/ContactPage.vue';
import SoftwarePage from 'pages/SoftwarePage.vue';
import LoginLandingPage from 'pages/LoginLandingPage.vue';
import BlogPage from 'pages/BlogPage.vue';
import AdminShellPage from 'pages/AdminShellPage.vue';
import ProjectManager from 'components/admin/ProjectManager.vue';
import ProjectEditor from 'components/admin/ProjectEditor.vue';
import UserManager from 'components/admin/UserManager.vue';
import UserEditor from 'components/admin/UserEditor.vue';
import FormsList from 'components/forms/FormsList.vue';
import FormFill from 'components/forms/FormFill.vue';
import SubmissionTable from 'components/forms/SubmissionTable.vue';
import NotFoundPage from 'pages/NotFoundPage.vue';

import { trackPageView } from 'src/analytics';
import { useAuthStore } from 'src/stores/auth';
import { authGuardDecision } from 'src/router/authGuard';

const routes = [
  // Core, always-visible pages.
  { path: '/', name: 'home', component: HomePage },
  { path: '/about', name: 'about', component: AboutPage },
  { path: '/services', name: 'services', component: ServicesPage },
  { path: '/projects', name: 'projects', component: ProjectsPage },
  // Single project case study. ProjectsPage links via
  // { name: 'project-detail', params: { id } }, so the name must match.
  { path: '/projects/:id', name: 'project-detail', component: ProjectDetailPage },
  { path: '/contact', name: 'contact', component: ContactPage },

  // Hidden destinations: always registered so they remain reachable by direct
  // URL (Requirements 2.2, 3.x, 12.1). Their nav links are flag-gated elsewhere.
  { path: '/software', name: 'software', component: SoftwarePage },
  { path: '/login', name: 'login', component: LoginLandingPage },
  { path: '/blog', name: 'blog', component: BlogPage },

  // Authenticated Admin_Shell landing (Requirement 10.1). Marked
  // `requiresAuth` so the Auth_Guard gates it; future admin screens plug in as
  // child routes without touching the guard (Requirement 10.3).
  //
  // Project_Manager Admin_Routes (Requirement 1.1) are registered as
  // `children` of this route (rather than top-level siblings) so that
  // navigating to /admin/projects, /admin/projects/new, or /admin/projects/:id
  // renders inside AdminShellPage's nested <router-view>, keeping the
  // persistent admin nav/header/logout region mounted instead of replacing the
  // whole shell. Each child still carries its own `meta.requiresAuth` so the
  // Auth_Guard marker remains visible on the route record itself. Route names
  // match how ProjectManager.vue navigates: { name: 'admin-project-new' } and
  // { name: 'admin-project-edit', params: { id } }. `props: true` passes the
  // `:id` route param to ProjectEditor as its `id` prop for edit mode.
  {
    path: '/admin',
    name: 'admin',
    component: AdminShellPage,
    meta: { requiresAuth: true },
    children: [
      {
        path: 'projects',
        name: 'admin-projects',
        component: ProjectManager,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
      {
        path: 'projects/new',
        name: 'admin-project-new',
        component: ProjectEditor,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
      {
        path: 'projects/:id',
        name: 'admin-project-edit',
        component: ProjectEditor,
        props: true,
        meta: { requiresAuth: true, requiresAdmin: true },
      },

      // Admin_User_Api screens (user and role management). These carry
      // `requiresAdmin` IN ADDITION to `requiresAuth`: any authenticated
      // employee may reach the Admin_Shell and the project screens, but only an
      // administrator may manage accounts and grant administrator rights. A
      // signed-in non-admin who navigates here is redirected to the shell
      // landing by the Auth_Guard, and the matching server endpoints are
      // independently gated by the `isAdmin` policy.
      {
        path: 'users',
        name: 'admin-users',
        component: UserManager,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
      {
        path: 'users/new',
        name: 'admin-user-new',
        component: UserEditor,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
      {
        path: 'users/:id',
        name: 'admin-user-edit',
        component: UserEditor,
        props: true,
        meta: { requiresAuth: true, requiresAdmin: true },
      },

      // Forms. Filling a form is open to any authenticated employee
      // (requiresAuth only); admin-only management (submissions, Stage 5) adds
      // requiresAdmin on its own routes. The server independently gates the
      // employee endpoints behind isAuthenticated and the admin ones behind isAdmin.
      {
        path: 'forms',
        name: 'forms',
        component: FormsList,
        meta: { requiresAuth: true },
      },
      {
        path: 'forms/:slug/fill',
        name: 'form-fill',
        component: FormFill,
        props: true,
        meta: { requiresAuth: true },
      },
      // Viewing submissions is admin-only (requiresAdmin), independently gated by
      // the isAdmin policy on the matching /administration endpoints.
      {
        path: 'forms/:slug/submissions',
        name: 'form-submissions',
        component: SubmissionTable,
        props: true,
        meta: { requiresAuth: true, requiresAdmin: true },
      },
    ],
  },

  // Catch-all 404 for unmatched routes (Requirement 13.4).
  { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundPage },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  // Reset scroll to the top on navigation so each page starts at its hero /
  // heading, while honouring in-page anchors and browser back/forward saved
  // positions.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    return { top: 0, left: 0 };
  },
});

// Auth_Guard — gates routes marked `meta.requiresAuth` and bounces
// authenticated visitors away from the Login page (design §6).
//
// Decisions (Property 9):
//   - Target requires auth AND session unauthenticated → cancel navigation and
//     redirect to the Login page, capturing the requested full path (including
//     query) as `redirect` so it can be resumed after sign-in. Because the
//     store's `isAuthenticated` getter already accounts for token expiry, an
//     expired token is treated as unauthenticated here (Requirements 6.1, 6.2,
//     6.3, 7.3).
//   - Target is the Login page AND session authenticated → redirect to the
//     Admin_Shell route (Requirement 6.5).
//   - Every other navigation is allowed, including all public routes and
//     authenticated access to admin routes (Requirements 6.4, 6.6, 12.1).
//   - Target requires admin AND the session is authenticated but not an
//     administrator → redirect to the Admin_Shell landing, which any
//     authenticated user may view. The role is advisory here: it keeps
//     non-admins out of screens that would not work for them, while the server
//     independently enforces the same rule on every admin endpoint.
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const decision = authGuardDecision(to, {
    authenticated: authStore.isAuthenticated,
    admin: authStore.isAdmin,
    superAdmin: authStore.isSuperAdmin,
  });

  if (decision) {
    next(decision);
    return;
  }

  next();
});

// Record a page view after each successful navigation. trackPageView is a
// no-op until analytics is configured with a measurement id (Requirement 13.6).
router.afterEach((to) => {
  trackPageView(to.fullPath);
});

// Session-expiry routing (Requirements 7.1, 7.2, 7.4).
//
// Called by the Api_Client response interceptor when a 403 is returned to a
// request that carried an Authorization header (i.e. a rejected/expired
// session). It idempotently clears the Session_Store and routes to the Login
// page with `reason=expired` (so the Login page shows the session-ended
// message, distinct from invalid credentials) and the active path captured as
// `redirect` so it can be resumed after re-authentication.
//
// Guarded against re-navigation: if we are already on the Login page it only
// clears the store and does not push a new navigation, so concurrent 403s do
// not cause redundant redirects.
export function handleSessionExpiry() {
  const authStore = useAuthStore();
  authStore.clear();

  const current = router.currentRoute.value;
  if (current && current.name === 'login') {
    return;
  }

  const redirect = current ? current.fullPath : '/';
  router.push({ name: 'login', query: { redirect, reason: 'expired' } });
}

export default router;
